import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export function Button({
  children,
  icon = 'arrow-right',
  onClick,
  busy = false,
  type = 'button',
}: {
  children: ReactNode;
  icon?: IconName | null;
  onClick?: () => void;
  busy?: boolean;
  type?: 'button' | 'submit';
}) {
  return (
    <button type={type} className="btn" onClick={busy ? undefined : onClick} aria-disabled={busy || undefined}>
      {children} {busy ? <Icon name="loader" size={16} className="spin" /> : icon ? <Icon name={icon} size={16} /> : null}
    </button>
  );
}

type Option = { id: string; label: string };

/**
 * A single-choice group with a roving tab stop. Arrow keys move and select
 * without committing; click, Enter or Space commits (the priority cards advance
 * on commit, so arrowing through them never skips the screen).
 */
export function RadioGroup({
  label,
  options,
  value,
  onSelect,
  onCommit,
  variant,
  error,
}: {
  label: string;
  options: Option[];
  value: string | null;
  onSelect: (id: string) => void;
  onCommit?: (id: string) => void;
  variant: 'tile' | 'card' | 'dot';
  error?: string | undefined;
}) {
  const labelId = useId();
  const errorId = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const current = Math.max(
    0,
    options.findIndex((option) => option.id === value),
  );

  function move(event: KeyboardEvent, index: number) {
    const delta = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const next = (index + delta + options.length) % options.length;
    onSelect(options[next]!.id);
    refs.current[next]?.focus();
  }

  return (
    <div>
      <div id={labelId} className="s15" style={{ fontWeight: 600 }}>
        {label}
      </div>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={error ? errorId : undefined}
        style={{ display: 'grid', gap: 8, marginTop: 10, gridTemplateColumns: variant === 'tile' ? 'repeat(2,minmax(0,1fr))' : undefined }}
      >
        {options.map((option, index) => {
          const on = option.id === value;
          return (
            <button
              key={option.id}
              ref={(node) => {
                refs.current[index] = node;
              }}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={index === current ? 0 : -1}
              className={`${variant === 'tile' ? 'tile' : 'card'}${on ? ' on' : ''}`}
              onKeyDown={(event) => move(event, index)}
              onClick={() => {
                onSelect(option.id);
                onCommit?.(option.id);
              }}
            >
              {variant === 'dot' ? <span className={`dot${on ? ' on' : ''}`} aria-hidden="true" /> : null}
              {variant === 'tile' ? option.label : <span style={{ flex: 1 }}>{option.label}</span>}
              {variant === 'card' ? <Icon name="arrow-right" size={16} style={{ color: '#4A4A4A' }} /> : null}
            </button>
          );
        })}
      </div>
      {error ? (
        <div id={errorId} className="err" style={{ marginTop: 8 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}

export function Checkbox({
  checked,
  onChange,
  children,
  className = 'tick',
  describedBy,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  className?: 'tick' | 'chk';
  describedBy?: string | undefined;
}) {
  return (
    <label className={className}>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} aria-describedby={describedBy} />
      <span className={`box${checked ? ' on' : ''}`} aria-hidden="true">
        <Icon name="check" size={14} />
      </span>
      <span>{children}</span>
    </label>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  children: (ids: { inputId: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}) {
  const inputId = useId();
  const hintId = useId();
  const errorId = useId();
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="fld">
      <label htmlFor={inputId} className="lbl">
        {label}
      </label>
      {children({ inputId, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <span id={hintId} className="s13 mute">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="err">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export type ComboOption = { value: string; label: string; detail?: string | null };

/**
 * ARIA 1.2 combobox with a listbox popup. Typing filters (or fetches) options;
 * arrow keys move the active option and Enter picks it. Free text is allowed, so
 * a job title not on the list is kept as typed.
 */
export function Combobox({
  inputId,
  value,
  onInput,
  onPick,
  options,
  describedBy,
  invalid,
  placeholder,
  autoComplete = 'off',
  loading = false,
}: {
  inputId: string;
  value: string;
  onInput: (text: string) => void;
  onPick: (option: ComboOption) => void;
  options: ComboOption[];
  describedBy?: string | undefined;
  invalid?: boolean;
  placeholder?: string;
  autoComplete?: string;
  loading?: boolean;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const visible = open && options.length > 0;

  function pick(option: ComboOption) {
    onPick(option);
    setOpen(false);
    setActive(-1);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(options.length - 1, index + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(0, index - 1));
    } else if (event.key === 'Enter' && visible && active >= 0) {
      event.preventDefault();
      pick(options[active]!);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        id={inputId}
        className="inp"
        role="combobox"
        aria-expanded={visible}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={visible && active >= 0 ? `${listId}-${active}` : undefined}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        aria-busy={loading || undefined}
        autoComplete={autoComplete}
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onInput(event.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
      />
      <ul id={listId} role="listbox" className="listbox" hidden={!visible} aria-label="Suggestions">
        {options.map((option, index) => (
          <li
            key={option.value}
            id={`${listId}-${index}`}
            role="option"
            aria-selected={index === active}
            className="option"
            onMouseDown={(event) => {
              event.preventDefault();
              pick(option);
            }}
          >
            <span>{option.label}</span>
            {option.detail ? <span className="s13 mute">{option.detail}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
